/**
 * MV2-192 — surface backend error messages to the UI.
 *
 * The FastAPI backend returns errors as a structured envelope (see
 * `monitoring-backend/app/core/responses.py:error_envelope`):
 *
 *     { "data": null,
 *       "error": { "code": "...", "message": "...", "details": {} },
 *       "request_id": "..." }
 *
 * Without unwrapping, axios surfaces the generic `"Request failed with status
 * code N"` and the actual reason (e.g. `"folder is not empty (3 files); delete
 * or move the files first"`) is invisible to operators. `extractApiError`
 * normalises any thrown / rejected value into a user-readable string by
 * preferring `error.message`, then `error.code`, then the underlying axios /
 * Error message, then a generic fallback.
 *
 * Most callers don't need to invoke this directly — the response interceptor
 * registered in `src/api/client.ts` rewrites `AxiosError.message` in place,
 * so existing `err instanceof Error ? err.message : String(err)` patterns
 * automatically pick up the friendly text. Reach for this helper when you
 * receive a non-Axios error and want consistent extraction.
 */

interface ApiErrorBody {
  error?: {
    code?: string;
    message?: string;
    details?: Record<string, unknown>;
  };
  request_id?: string;
}

interface ResponseLike {
  data?: ApiErrorBody;
}

interface ErrorLike {
  response?: ResponseLike;
  message?: unknown;
}

interface StatusErrorLike {
  response?: { status?: unknown };
}

/**
 * The HTTP status off a rejected request, or `null` when the failure never got
 * a response (network error, timeout, abort).
 *
 * The response interceptor in `client.ts` rewrites `AxiosError.message` but
 * deliberately leaves `response.status` alone, so callers that need to
 * distinguish "expected absence" from "something is wrong" can branch on it.
 * MV2-616 is the motivating case: a site with no `control_map.json` yet is a
 * normal pre-commissioning state, and a 404 there must not be reported to the
 * operator as an error the way a 422 or 500 should be.
 */
export function apiErrorStatus(e: unknown): number | null {
  if (e && typeof e === "object") {
    const status = (e as StatusErrorLike).response?.status;
    if (typeof status === "number") return status;
  }
  return null;
}

export function extractApiError(e: unknown): string {
  if (e && typeof e === "object") {
    const body = (e as ErrorLike).response?.data;
    const envelopeMessage = body?.error?.message;
    if (typeof envelopeMessage === "string" && envelopeMessage.length > 0) {
      return envelopeMessage;
    }
    const envelopeCode = body?.error?.code;
    if (typeof envelopeCode === "string" && envelopeCode.length > 0) {
      return envelopeCode;
    }
    const message = (e as ErrorLike).message;
    if (typeof message === "string" && message.length > 0) {
      return message;
    }
  }
  return "Request failed.";
}

/** The API envelope's `request_id`, when the failed response carried one. */
export function apiRequestId(e: unknown): string | null {
  if (e && typeof e === "object") {
    const id = (e as ErrorLike).response?.data?.request_id;
    if (typeof id === "string" && id.length > 0) return id;
  }
  return null;
}

/**
 * The envelope's `error.details.code` — the specific reason behind a 422
 * (`tariff_zone_not_found`, `tariff_coverage_gap`, …) that a form maps to a
 * sentence on the field that caused it. `null` when the failure carried no
 * envelope or no coded detail.
 */
export function apiErrorDetailCode(e: unknown): string | null {
  if (e && typeof e === "object") {
    const details = (e as ErrorLike).response?.data?.error?.details;
    const code = details?.code;
    if (typeof code === "string" && code.length > 0) return code;
  }
  return null;
}
